"use client";

import { Train, CalendarDays, MapPin, Clock, Armchair } from "lucide-react";
import { format } from "date-fns";
import { formatConsistentDate } from "@/utils/timezone-date-utils";
import { quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { iRailLegDetails } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { ImageGallery } from "@/components/common/image-gallery/image-gallery";
import { HtmlContentWithReadMore } from "@/components/common/html-content-with-read-more";

export interface RailItineraryInnerProps extends iRailLegDetails {
  primaryColor?: string;
  day?: number;
}

export function RailItineraryInner(props: RailItineraryInnerProps) {
  const primaryColor = quoteThemeCssVars.primary;
  const lightColor = quoteThemeCssVars.primary10;

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return formatConsistentDate(date, "EEE, d MMM yyyy");
  };

  const formatTime = (time: string | null) => {
    if (!time) return "";
    return format(new Date(`1970-01-01T${time}`), "HH:mm");
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Rail Header */}
      {(props.operator ||
        props.trainNumber ||
        props.seatClass ||
        props.seatNumber) && (
        <div className="mb-4 sm:mb-6">
          {props.operator && (
            <h3 className="text-lg sm:text-xl font-semibold mb-2">{props.operator}</h3>
          )}
          {props.trainNumber && (
            <p className="text-sm sm:text-base text-gray-600">Train {props.trainNumber}</p>
          )}
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-2 sm:mt-3">
            {(props.seatClass || props.seatNumber) && (
              <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                <Armchair
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  style={{ color: primaryColor }}
                />
                <span className="hidden sm:inline">{props.seatClass}</span>
                <span className="sm:hidden">{props.seatClass?.split(" ")[0]}</span>
                {props.seatNumber && (
                  <>
                    <span className="hidden sm:inline"> - Seat {props.seatNumber}</span>
                    <span className="sm:hidden"> - {props.seatNumber}</span>
                  </>
                )}
              </span>
            )}
            {props.platformNumber && (
              <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                <Train className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: primaryColor }} />
                <span className="hidden sm:inline">Platform {props.platformNumber}</span>
                <span className="sm:hidden">Plat {props.platformNumber}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Overview/Body */}
      {props.body && (
        <HtmlContentWithReadMore
          html={props.body}
          className="mb-4 sm:mb-6"
          maxWords={40}
        />
      )}

      {/* Gallery */}
      {props.gallery && props.gallery.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <ImageGallery images={props.gallery} />
        </div>
      )}

      {/* Journey Details */}
      {(props.departureDate ||
        props.departureTime ||
        props.departureStation ||
        props.arrivalDate ||
        props.arrivalTime ||
        props.arrivalStation) && (
        <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-6">
          {/* Departure */}
          {(props.departureDate ||
            props.departureTime ||
            props.departureStation) && (
            <div className="space-y-2 sm:space-y-4">
              {props.departureDate && (
                <div className="flex items-start gap-1.5 sm:gap-3">
                  <CalendarDays
                    className="w-3.5 h-3.5 sm:w-5 sm:h-5 mt-0.5 sm:mt-1 flex-shrink-0"
                    style={{ color: primaryColor }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-xs sm:text-base">
                      <span className="hidden sm:inline">Departure</span>
                      <span className="sm:hidden">Dep</span>
                    </div>
                    <div className="text-xs sm:text-base truncate">
                      {formatDate(props.departureDate)}
                    </div>
                  </div>
                </div>
              )}
              {props.departureTime && (
                <div className="flex items-start gap-1.5 sm:gap-3">
                  <Clock
                    className="w-3.5 h-3.5 sm:w-5 sm:h-5 mt-0.5 sm:mt-1 flex-shrink-0"
                    style={{ color: primaryColor }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-xs sm:text-base">
                      <span className="hidden sm:inline">Time</span>
                      <span className="sm:hidden">Time</span>
                    </div>
                    <div className="text-xs sm:text-base">{formatTime(props.departureTime)}</div>
                  </div>
                </div>
              )}
              {props.departureStation && (
                <div className="flex items-start gap-1.5 sm:gap-3">
                  <MapPin
                    className="w-3.5 h-3.5 sm:w-5 sm:h-5 mt-0.5 sm:mt-1 flex-shrink-0"
                    style={{ color: primaryColor }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-xs sm:text-base">
                      <span className="hidden sm:inline">Station</span>
                      <span className="sm:hidden">Station</span>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 truncate">
                      {props.departureStation}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Arrival */}
          {(props.arrivalDate ||
            props.arrivalTime ||
            props.arrivalStation) && (
            <div className="space-y-2 sm:space-y-4">
              {props.arrivalDate && (
                <div className="flex items-start gap-1.5 sm:gap-3">
                  <CalendarDays
                    className="w-3.5 h-3.5 sm:w-5 sm:h-5 mt-0.5 sm:mt-1 flex-shrink-0"
                    style={{ color: primaryColor }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-xs sm:text-base">
                      <span className="hidden sm:inline">Arrival</span>
                      <span className="sm:hidden">Arr</span>
                    </div>
                    <div className="text-xs sm:text-base truncate">
                      {formatDate(props.arrivalDate)}
                    </div>
                  </div>
                </div>
              )}
              {props.arrivalTime && (
                <div className="flex items-start gap-1.5 sm:gap-3">
                  <Clock
                    className="w-3.5 h-3.5 sm:w-5 sm:h-5 mt-0.5 sm:mt-1 flex-shrink-0"
                    style={{ color: primaryColor }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-xs sm:text-base">
                      <span className="hidden sm:inline">Time</span>
                      <span className="sm:hidden">Time</span>
                    </div>
                    <div className="text-xs sm:text-base">{formatTime(props.arrivalTime)}</div>
                  </div>
                </div>
              )}
              {props.arrivalStation && (
                <div className="flex items-start gap-1.5 sm:gap-3">
                  <MapPin
                    className="w-3.5 h-3.5 sm:w-5 sm:h-5 mt-0.5 sm:mt-1 flex-shrink-0"
                    style={{ color: primaryColor }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-xs sm:text-base">
                      <span className="hidden sm:inline">Station</span>
                      <span className="sm:hidden">Station</span>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 truncate">
                      {props.arrivalStation}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Duration Badge */}
      {Boolean(props.duration) && (
        <div>
          <span
            className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm"
            style={{
              backgroundColor: lightColor,
              color: primaryColor,
            }}
          >
            <span className="hidden sm:inline">
              {props.duration! >= 60
                ? `${Math.floor(props.duration! / 60)}h ${props.duration! % 60}m journey`
                : `${props.duration} minutes journey`}
            </span>
            <span className="sm:hidden">
              {props.duration! >= 60
                ? `${Math.floor(props.duration! / 60)}h ${props.duration! % 60}m`
                : `${props.duration}m`}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}
